import React, { useCallback, useEffect, useState } from 'react';
import NeverOverpayNav from './NeverOverpayNav';
import { neveroverpayApi } from './neveroverpayApi';
import ConsentGate, { CONSENT_CATALOG, consentActive } from './NeverOverpayConsent';
import { openPlaidLink } from './plaidLink';
import GmailConnectCard from './GmailConnectCard';
import WeeklyDigestPanel from './WeeklyDigestPanel';
import './neveroverpay.css';
import './neveroverpay-ui.css';

function dollarsToCents(value) {
  const n = Number(String(value).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

export default function NeverOverpaySettings() {
  const [gmail, setGmail] = useState(null);
  const [plaid, setPlaid] = useState(null);
  const [notes, setNotes] = useState([]);
  const [consents, setConsents] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const [minValue, setMinValue] = useState('10');
  const [renewalDays, setRenewalDays] = useState('30');
  const [minConf, setMinConf] = useState('80');
  const [notifyEmail, setNotifyEmail] = useState(false);

  const refresh = useCallback(async () => {
    const [hh, mail, bank, ntf, grants] = await Promise.all([
      neveroverpayApi.household(),
      neveroverpayApi.gmailStatus().catch(() => null),
      neveroverpayApi.plaidStatus().catch(() => null),
      neveroverpayApi.notifications().catch(() => ({ notifications: [] })),
      neveroverpayApi.consents().catch(() => ({ active: [], grants: [] })),
    ]);
    setGmail(mail);
    setPlaid(bank);
    setNotes(ntf.notifications || []);
    setConsents(grants);
    const policy = hh?.policy || {};
    setMinValue(String(((policy.minimumAlertValueCents || 1000) / 100).toFixed(2)));
    setRenewalDays(String(policy.renewalAlertDays || 30));
    setMinConf(String(Math.round((policy.notifyMinConfidence || 0.8) * 100)));
    setNotifyEmail(Boolean(policy.notifyEmail));
  }, []);

  useEffect(() => {
    refresh().catch((err) => setError(err.message || 'Could not load settings'));
  }, [refresh]);

  async function run(fn, ok) {
    setBusy(true);
    setSaved('');
    try {
      await fn();
      await refresh();
      setError('');
      if (ok) setSaved(ok);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveAlerts(event) {
    event.preventDefault();
    await run(
      () => neveroverpayApi.updateAlerts({
        minimumAlertValueCents: dollarsToCents(minValue),
        renewalAlertDays: Number(renewalDays) || 30,
        notifyMinConfidence: Math.min(1, Math.max(0, (Number(minConf) || 80) / 100)),
        notifyEmail,
        alwaysRequireSendApproval: true,
      }),
      'Alert settings saved.',
    );
  }

  async function connectGmail(send) {
    await run(async () => {
      await neveroverpayApi.grantConsent(send ? 'gmail_send' : 'gmail_read');
      const row = await neveroverpayApi.gmailConnectUrl(send);
      window.location.href = row.url;
    });
  }

  async function connectPlaid() {
    await run(async () => {
      await neveroverpayApi.grantConsent('bank_match');
      const tokenRow = await neveroverpayApi.plaidLinkToken();
      const publicToken = await openPlaidLink({ linkToken: tokenRow.link_token });
      if (!publicToken) return;
      await neveroverpayApi.plaidExchange(publicToken);
    }, 'Bank connected. We only read transactions you authorize — we do not move money.');
  }

  async function exportData() {
    await run(async () => {
      const row = await neveroverpayApi.exportHousehold();
      const blob = new Blob([JSON.stringify(row, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `neveroverpay-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'Export downloaded.');
  }

  async function deleteAll() {
    if (!window.confirm('Delete this household’s recoveries, ledger, and connected accounts? This cannot be undone.')) {
      return;
    }
    await run(async () => {
      await neveroverpayApi.deleteHousehold();
      window.location.href = '/';
    });
  }

  return (
    <div className="np-root">
      <div className="np-wrap">
        <NeverOverpayNav mark="Settings" />
        {error ? <div className="np-error" style={{ marginBottom: 16 }}>{error}</div> : null}
        {saved ? <p className="np-muted" style={{ marginBottom: 16 }}>{saved}</p> : null}

        <h1 style={{ fontSize: 32, letterSpacing: '-0.03em', margin: '8px 0 24px' }}>Notifications &amp; privacy</h1>

        <div style={{ marginBottom: 24 }} data-testid="settings-gmail">
          <GmailConnectCard gmail={gmail} consents={consents} busy={busy} onChanged={refresh} />
        </div>

        <div style={{ marginBottom: 24 }} data-testid="settings-digest">
          <WeeklyDigestPanel
            gmail={gmail}
            showPreview={false}
            onNeedGmailSend={async () => {
              setBusy(true);
              try {
                await neveroverpayApi.grantConsent('gmail_send');
                const row = await neveroverpayApi.gmailConnectUrl(true);
                window.location.href = row.url;
              } catch (err) {
                setError(err.message);
                setBusy(false);
              }
            }}
          />
        </div>

        <form className="np-card" onSubmit={saveAlerts} style={{ marginBottom: 24 }}>
          <h3>When we notify you</h3>
          <p className="np-muted">In-app alerts only fire at 80% confidence or higher, and only for cases at or above your dollar floor. Sending still always needs your exact OK.</p>
          <label className="np-muted" style={{ display: 'block', marginTop: 16 }}>Minimum value ($)</label>
          <input className="np-input" value={minValue} onChange={(e) => setMinValue(e.target.value)} />
          <label className="np-muted" style={{ display: 'block', marginTop: 12 }}>Renewal warning (days before)</label>
          <input className="np-input" value={renewalDays} onChange={(e) => setRenewalDays(e.target.value)} />
          <label className="np-muted" style={{ display: 'block', marginTop: 12 }}>Minimum confidence (%)</label>
          <input className="np-input" value={minConf} onChange={(e) => setMinConf(e.target.value)} />
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} />
            Also email me when a high-confidence case is ready
          </label>
          <div className="np-actions">
            <button type="submit" className="np-btn" disabled={busy}>Save alerts</button>
          </div>
        </form>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Recent alerts</h3>
          {notes.length === 0 ? <p className="np-muted">No in-app alerts yet.</p> : notes.map((row) => (
            <div className="np-case-row" key={row.id}>
              <div>
                <div className="np-pill">{row.status}</div>
                <h3 style={{ marginTop: 8 }}>{row.title}</h3>
                <p className="np-muted">{row.body}</p>
              </div>
              {row.status !== 'read' ? (
                <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.markNotificationRead(row.id))}>
                  Mark read
                </button>
              ) : null}
            </div>
          ))}
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Connected accounts</h3>
          <p className="np-muted">Gmail stays read-only until you grant send on Approve &amp; Send. Bank connect uses Plaid Link in every country Plaid supports. We do not move money or make payments.</p>
          <p style={{ marginTop: 12 }}>Gmail: {gmail?.connected ? (gmail.email || 'connected') : 'not connected'}{gmail?.can_send ? ' · can send' : ''}</p>
          {!gmail?.connected && !gmail?.configured ? (
            <p className="np-muted" style={{ marginTop: 8 }}>
              To turn Gmail on: set the same <code>GOOGLE_CLIENT_ID</code> / <code>GOOGLE_CLIENT_SECRET</code> Snagcart uses
              (or paste them in <a href="/admin">Admin</a>), then return here.
              Or skip Gmail and <a href="/app">paste a company email on Home</a>.
            </p>
          ) : null}
          {!gmail?.connected ? (
            <ConsentGate
              kind="gmail_read"
              consents={consents}
              busy={busy}
              blocked={gmail?.configured ? '' : 'Gmail connect uses the same Google OAuth client as Continue with Google. Set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET (or paste them in Admin), then return here. Until then, paste a company email on Home.'}
              actionLabel="Allow and connect Gmail"
              onAllow={() => connectGmail(false)}
            >
              {gmail?.configured ? (
                <div className="np-actions">
                  <button type="button" className="np-btn" disabled={busy} onClick={() => connectGmail(false)}>Connect Gmail</button>
                </div>
              ) : null}
            </ConsentGate>
          ) : null}
          <p>Bank: {plaid?.connected ? (plaid.status === 'PENDING_DISCONNECT' || plaid.status === 'NEEDS_REAUTH' ? 'needs reconnect' : 'Plaid connected') : (plaid?.configured ? 'ready to connect' : 'not configured (CSV still works)')}</p>
          {plaid?.consent_warning ? (
            <div className="np-error" style={{ marginTop: 12 }}>{plaid.consent_warning}</div>
          ) : null}
          {!plaid?.connected || plaid?.consent_warning ? (
            <ConsentGate
              kind="bank_match"
              consents={consents}
              busy={busy}
              actionLabel={plaid?.configured ? 'Connect Bank Securely' : 'Allow bank matching'}
              onAllow={async () => {
                if (plaid?.configured) {
                  await connectPlaid();
                  return;
                }
                await run(() => neveroverpayApi.grantConsent('bank_match'), 'Bank matching allowed. Upload a CSV on Home.');
              }}
            >
              {plaid?.configured ? (
                <div className="np-actions">
                  <button type="button" className="np-btn" disabled={busy} onClick={connectPlaid}>
                    {plaid?.consent_warning ? 'Reconnect Bank Securely' : 'Connect Bank Securely'}
                  </button>
                </div>
              ) : (
                <p className="np-muted">Bank matching is on. Upload a CSV on <a href="/app">Home</a>.</p>
              )}
            </ConsentGate>
          ) : null}
          <div className="np-actions">
            {gmail?.connected && !gmail?.can_send ? (
              <ConsentGate
                kind="gmail_send"
                consents={consents}
                busy={busy}
                actionLabel="Allow and continue to Google"
                onAllow={() => connectGmail(true)}
              >
                <button type="button" className="np-btn ghost" disabled={busy} onClick={() => connectGmail(true)}>Allow Gmail send</button>
              </ConsentGate>
            ) : null}
            {gmail?.connected ? (
              <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.gmailDisconnect(), 'Gmail disconnected.')}>
                Disconnect Gmail
              </button>
            ) : null}
            {plaid?.connected ? (
              <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.plaidSync(), 'Bank transactions refreshed.')}>
                Sync bank now
              </button>
            ) : null}
          </div>
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Your permissions</h3>
          <p className="np-muted">These are the yeses you gave us. Turning one off stops that access. Disconnecting Gmail or bank also turns the matching permission off.</p>
          {['gmail_read', 'gmail_send', 'bank_match', 'documents'].map((kind) => {
            const on = consentActive(consents, kind);
            const spec = CONSENT_CATALOG[kind];
            return (
              <div className="np-case-row" key={kind}>
                <div>
                  <h3 style={{ marginTop: 0 }}>{spec.title}</h3>
                  <p className="np-muted">{on ? 'On' : 'Off'}</p>
                </div>
                {on ? (
                  <button
                    type="button"
                    className="np-btn ghost"
                    disabled={busy}
                    onClick={() => run(() => neveroverpayApi.revokeConsent(kind), 'Permission turned off.')}
                  >
                    Turn off
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="np-card" style={{ marginBottom: 24 }}>
          <h3>Privacy</h3>
          <p className="np-muted">Export is a JSON copy of this household’s cases and ledger. Delete removes recoveries and connected accounts for this household.</p>
          <div className="np-actions">
            <button type="button" className="np-btn ghost" disabled={busy} onClick={exportData}>Download my data</button>
            <button type="button" className="np-btn ghost" disabled={busy} onClick={deleteAll}>Delete my data</button>
          </div>
        </div>
      </div>
    </div>
  );
}
